import { Button, Stack, TextField, Typography } from '@mui/material';
import { useFirebase } from 'contexts/FirebaseContext';
import { UserCredential, createUserWithEmailAndPassword } from 'firebase/auth';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface RegisterProps {
  onSuccess: (username: string, token: string | null, userCredential: UserCredential) => void;
}

export function Register(props: RegisterProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string>('');

  // TODO: check these values (boolean)
  //
  // containsLowercaseLetter != null && containsLowercaseLetter
  // containsNonAlphanumericCharacter
  // containsNumericCharacter
  // containsUppercaseLetter
  // isValid
  // meetsMaxPasswordLength
  // meetsMinPasswordLength

  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,30}$/;
  const auth = useFirebase().auth;
  const isPasswordValid = passwordRegex.test(password);
  const isConfirmPasswordValid = password === confirmPassword;
  const isFormValid = isPasswordValid && isConfirmPasswordValid && username !== '';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, username, password);
      const token = await userCredential.user?.getIdToken();
      props.onSuccess(username, token, userCredential);
    } catch (error: unknown) {
      if (error instanceof Error) {
        if ('Firebase: Error (auth/email-already-in-use).' == error.message) {
          setError(
            'This email address is already in use. Please use a different email address or log in.'
          );
        } else if ('Firebase: Error (auth/weak-password).' == error.message) {
          setError('The password is too weak. Please choose a stronger password.');
        } else {
          setError('Unknown error. Could not register user');
        }
      } else {
        console.log(error);
        setError('Unknown error. Could not register user');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={1}>
        <Typography variant="h3" textAlign="center" sx={{ pb: 2 }}>
          {t('register', 'Register')}
        </Typography>
        <TextField
          label="Email"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={!isPasswordValid && password != ''}
          helperText={
            !isPasswordValid &&
            password != '' &&
            t(
              'passwordRequirements',
              'The password must be 6–30 characters long and include letters, numbers, and at least one special character.'
            )
          }
        />
        <TextField
          label={t('confirmPassword', 'Confirm Password')}
          type="password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          error={!isConfirmPasswordValid && confirmPassword != ''}
          helperText={
            !isConfirmPasswordValid &&
            confirmPassword != '' &&
            t('passwordMismatch', 'The passwords do not match.')
          }
        />
        <Button type="submit" variant="contained" disabled={!isFormValid}>
          {t('register', 'Register')}
        </Button>
        {error && <Typography color="error">{error}</Typography>}
      </Stack>
    </form>
  );
}
